import { ConsoleLogger } from '@proposify/frontend-observability';
import type { ReactNode } from 'react';
import type React from 'react';
import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { ThreadData, CommentData, CommentBody } from '@liveblocks/client';
import { useMergedThreads } from './MergedThreadsProvider';
import { useGlobalRoomOperations } from './GlobalRoomOperationsManager';
import { RoomType } from './RoomRegistrar';
import { useViewerInfo } from 'components/preview/identify-viewer/useViewerInfo';
import { parseLiveblocksUser } from 'components/editor/GridDndEditor/TiptapEditor/helpers/parseLiveblocksUser';
import { useSearchParams } from 'hooks/useSearchParams';
import { resolveThreadDeepLink } from './resolveThreadDeepLink';

export type LiveblocksThread = ThreadData & { __origin: RoomType };

export interface CommentsContextType {
  // Thread management - now uses Liveblocks native types
  threads?: LiveblocksThread[];
  isLoading: boolean;

  // User permissions
  canManageAllThreads: boolean;
  canEditComment: (comment: CommentData) => boolean;
  canDeleteComment: (comment: CommentData) => boolean;
  canCreateInternalThread: boolean;
  userType: RoomType;
  isPrivate: boolean;

  // UI state
  activeThreadId: string | null;
  setActiveThreadId: (threadId: string | null) => void;
  focusThread: (threadId: string) => Promise<void>;
  autoFocusThreadId: string | null;
  clearAutoFocusThreadId: () => void;
  toggleComments: () => void;
  commentsEnabled: boolean;

  // Room operations
  createThread: (commentBody: CommentBody, roomType: RoomType, blockId: string | null) => Promise<{ id: string }>;
  addComment: (threadId: string, body: CommentBody, roomType: RoomType) => Promise<{ id: string }>;
  editComment: (threadId: string, commentId: string, body: CommentBody, roomType: RoomType) => Promise<void>;
  deleteComment: (threadId: string, commentId: string, roomType: RoomType) => Promise<void>;
  unResolveThread: (threadId: string, roomType: RoomType) => Promise<void>;
  resolveThread: (threadId: string, roomType: RoomType) => Promise<void>;
  deleteThread: (threadId: string, roomType: RoomType) => Promise<void>;
}

const CommentsContext = createContext<CommentsContextType | null>(null);

interface CommentsProviderProps {
  isPrivate: boolean;
  children: ReactNode;
  currentUserEmail?: string;
  currentUserId?: string;
}

export const CommentsProvider: React.FC<CommentsProviderProps> = ({ isPrivate, children, currentUserEmail, currentUserId }) => {
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [autoFocusThreadId, setAutoFocusThreadId] = useState<string | null>(null);
  const [commentsEnabled, setCommentsEnabled] = useState(false);
  const { threads, isLoading: isThreadsLoading } = useMergedThreads();
  const [searchParams, setSearchParams] = useSearchParams();

  const { executeInRoom, canExecuteInRoom } = useGlobalRoomOperations();
  const { viewer } = useViewerInfo();

  const toggleComments = useCallback(() => {
    setCommentsEnabled((prev) => !prev);
  }, []);

  const isCommentOwner = useCallback(
    (user: ReturnType<typeof parseLiveblocksUser>) => {
      if (isPrivate) {
        if (user.type !== 'internal') return false;

        return 'userUid' in user ? user.userId === currentUserId : user.userEmail === currentUserEmail;
      }

      if (user.type !== 'external') return false;

      return 'viewerId' in user ? user.viewerId === viewer?.id : user.userEmail === viewer?.email;
    },
    [isPrivate, currentUserEmail, currentUserId, viewer]
  );

  const canEditComment = useCallback(
    (comment: CommentData) => {
      const user = parseLiveblocksUser(comment.userId);

      return isCommentOwner(user);
    },
    [isCommentOwner]
  );

  const canDeleteComment = useCallback(
    (comment: CommentData) => {
      const user = parseLiveblocksUser(comment.userId);

      return isCommentOwner(user);
    },
    [isCommentOwner]
  );

  const canManageAllThreads = isPrivate;
  const canCreateInternalThread = isPrivate;

  const checkCanExecuteInRoom = useCallback(
    (roomType: RoomType) => {
      const canExecute = canExecuteInRoom(roomType);
      if (!canExecute) {
        ConsoleLogger.error(`Cannot execute in ${roomType} room - not available`);
      }
      return canExecute;
    },
    [canExecuteInRoom]
  );

  const createThread = useCallback(
    async (commentBody: CommentBody, roomType: RoomType, blockId: string | null) => {
      if (checkCanExecuteInRoom(roomType)) {
        return executeInRoom(roomType, 'createThread', commentBody, blockId);
      }
      return { id: '' };
    },
    [executeInRoom, checkCanExecuteInRoom]
  );

  const addComment = useCallback(
    async (threadId: string, body: CommentBody, roomType: RoomType) => {
      if (checkCanExecuteInRoom(roomType)) {
        return executeInRoom(roomType, 'addComment', threadId, body);
      }
      return { id: '' };
    },
    [executeInRoom, checkCanExecuteInRoom]
  );

  const editComment = useCallback(
    async (threadId: string, commentId: string, body: CommentBody, roomType: RoomType) => {
      if (checkCanExecuteInRoom(roomType)) {
        return executeInRoom(roomType, 'editComment', threadId, commentId, body);
      }
    },
    [executeInRoom, checkCanExecuteInRoom]
  );

  const deleteComment = useCallback(
    async (threadId: string, commentId: string, roomType: RoomType) => {
      if (checkCanExecuteInRoom(roomType)) {
        return executeInRoom(roomType, 'deleteComment', threadId, commentId);
      }
    },
    [executeInRoom, checkCanExecuteInRoom]
  );

  const resolveThread = useCallback(
    async (threadId: string, roomType: RoomType) => {
      if (checkCanExecuteInRoom(roomType)) {
        return executeInRoom(roomType, 'resolveThread', threadId);
      }
    },
    [executeInRoom, checkCanExecuteInRoom]
  );

  const unResolveThread = useCallback(
    async (threadId: string, roomType: RoomType) => {
      if (checkCanExecuteInRoom(roomType)) {
        return executeInRoom(roomType, 'unResolveThread', threadId);
      }
    },
    [executeInRoom, checkCanExecuteInRoom]
  );

  const deleteThread = useCallback(
    async (threadId: string, roomType: RoomType) => {
      if (checkCanExecuteInRoom(roomType)) {
        return executeInRoom(roomType, 'deleteThread', threadId);
      }
    },
    [executeInRoom, checkCanExecuteInRoom]
  );

  const focusThread = useCallback(
    async (threadId: string) => {
      setActiveThreadId(threadId);
    },
    [setActiveThreadId]
  );

  const clearAutoFocusThreadId = useCallback(() => {
    setAutoFocusThreadId(null);
  }, []);

  useEffect(() => {
    const threadIdParam = searchParams.get('threadId');
    const openCommentsParam = searchParams.get('openComments');
    const action = resolveThreadDeepLink(threadIdParam, openCommentsParam, threads, isThreadsLoading);

    if (action.type === 'noop' || action.type === 'wait') {
      return;
    }

    setCommentsEnabled(true);

    if (action.type === 'focus') {
      focusThread(action.threadId);
      setAutoFocusThreadId(action.threadId);
    } else {
      setActiveThreadId(null);
      setAutoFocusThreadId(null);
      if (action.threadId) {
        ConsoleLogger.warn('Thread from URL not found, opening comments panel without selection', {
          threadId: action.threadId,
        });
      }
    }

    const newParams = new URLSearchParams(searchParams);
    newParams.delete('threadId');
    newParams.delete('openComments');
    setSearchParams(newParams, { replace: true });
  }, [searchParams, threads, isThreadsLoading, focusThread, setSearchParams]);

  const contextValue: CommentsContextType = {
    threads: threads || [],
    isLoading: isThreadsLoading,
    isPrivate,
    canManageAllThreads,
    canEditComment,
    canDeleteComment,
    canCreateInternalThread,
    userType: isPrivate ? RoomType.PRIVATE : RoomType.PUBLIC,
    activeThreadId,
    focusThread,
    autoFocusThreadId,
    clearAutoFocusThreadId,
    setActiveThreadId,
    createThread,
    addComment,
    editComment,
    deleteComment,
    resolveThread,
    unResolveThread,
    deleteThread,
    toggleComments,
    commentsEnabled,
  };

  return <CommentsContext.Provider value={contextValue}>{children}</CommentsContext.Provider>;
};

export const useComments = (): CommentsContextType => {
  const context = useContext(CommentsContext);
  if (!context) {
    return {
      threads: [],
      isLoading: false,
      isPrivate: false,
      canManageAllThreads: false,
      canEditComment: () => false,
      canDeleteComment: () => false,
      canCreateInternalThread: false,
      userType: RoomType.PUBLIC,
      activeThreadId: null,
      setActiveThreadId: () => {
        // Do nothing
      },
      focusThread: () => Promise.resolve(),
      autoFocusThreadId: null,
      clearAutoFocusThreadId: () => {
        // Do nothing
      },
      toggleComments: () => {
        // Do nothing
      },
      commentsEnabled: false,
      createThread: () => Promise.resolve({ id: '' }),
      addComment: () => Promise.resolve({ id: '' }),
      editComment: () => Promise.resolve(),
      deleteComment: () => Promise.resolve(),
      unResolveThread: () => Promise.resolve(),
      resolveThread: () => Promise.resolve(),
      deleteThread: () => Promise.resolve(),
    };
  }
  return context;
};
